import IconButton from "components/blocks/IconButton";
import GridView from "components/GridView";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import { useCookieContext } from "contexts";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import { useTheme } from "contexts/theme/ThemeContext";
import useGridData from "hooks/useGridData";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { JudgeResult } from "services/judgesService/models/judgesTypes";
import {RefereeUserResult} from "../../../../services/RefereeService/Model/RefereeTypes";
import {RewardResponse} from "../../../../services/RewardsService/models";
import { useTitle } from "hooks/useTitle";

const JudgesGridView = () => {
  const { t } = useTranslation("Judges");
  useTitle(t("judgesInfo"));
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { isAr } = useCookieContext();
  const { setTheme, resetTheme } = useTheme();
    
  const { loading, gridData, totalCount, exportToExcel } = useGridData<"refereesService", JudgeResult>({
    service: "refereesService",
    endpoint: "getAllReferees",
    exportExcelEndpoint: "exportRefereesToExcelAsync",
    exportFileName: "referees",
    localizationNameSpace: "Judges",
  });

  useEffect(() => {
      
    setBreadcrumbs([
      {
        localizationKey: "judgesInfo",
        path: "/home/judgesManagement/judges",
        menuItemId: "judges",
      },
    ]);
  }, []);

  useEffect(() => {
    setTheme({ homeLayout: { main: { bgColorClass: "", roundedClass: "" } } });

    return () => {
      resetTheme();
    };
  }, []);

  const gridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: "id",
        displayName: t("judgeName"),
        showOnMobile: true,
        sortable: true,
        filterable: true,
        render: (row: RefereeUserResult) => {
          return (
            <button
              className="btn btn-link"
              onClick={() => navigate(`/home/judgesManagement/judges/view?judgeId=${row.id}`)}
            >
              {isAr ? row?.arabicName  ??"" : row?.englishName??""}
            </button>
          );
        },
      },
      {
        field: "username",
        displayName: t("username"),
      },
      {
        field: "completedTasks",
        displayName: t("completedTasks"),
        render: (row: RefereeUserResult) => {
          return <>  {row?.taskCount ?? 0}</>;
        },
      },
      {
        field: "joinDate",
        displayName: t("joinDate"),
        render: (row: RefereeUserResult) => {
          return toSystemThemeDateFormat(row.createdOn);
        },
      },
      {
        field: "IsActive",
        displayName: t("Common:status"),
        showOnMobile: true,
        render(row: RefereeUserResult) {
          const statusClassName = row.isActive
            ? "bg-primary bg-opacity-10 text-primary"
            : "bg-danger bg-opacity-10 text-danger";
          return (
            <span className={`badge rounded-4 ${statusClassName} py-2`}>
              {t(row.isActive ? "Common:active" : "Common:inactive")}
            </span>
          );
        },
      },
      {
        field: "id",
        displayName: t("Common:actions"),
        render: (row: RefereeUserResult) => {
          return (
            <IconButton
              title={t("Common:edit")}
              icon="icon-edit"
              fitToIconWidth
              innerIconColor="dark"
              size="md"
              onClick={() => {
                navigate(`/home/editUserProfile/${row.id}`);
              }}
            />
          );
        },
      },
    ],
    [t, toSystemThemeDateFormat],
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("judges"),
    singularName: t("judge"),
    totalCount: totalCount,
    onExportClick: exportToExcel,
  };

  return (
    <div className="d-flex flex-column h-100 gap-3">
      <div
        className="flex-1 bg-white rounded-4 overflow-y-auto"
        style={{
          marginInline: -20,
          marginTop: -20,
          padding: 20,
        }}
      >
        <GridView
          loading={loading}
          gridProps={{
            data: gridData ?? [],
            gridSchema,
          }}
          viewHeaderProps={viewHeader}
        />
      </div>
      <div className="bg-white rounded-4 p-3" style={{ marginInline: -20, marginBottom: -20 }}>
        <b className="text-danger px-1">*</b>
        {t("page-note")}
      </div>
    </div>
  );
};

export default JudgesGridView;
